import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppPayFrequencySelect from '@nxsys/components/AppPayFrequencySelect';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import { paymentModelOptionsIreland } from '@nxsys/constants/AppConstants';
import {
  defaultsBusinessTypeOptions,
  titleOptions,
} from '@nxsys/constants/prospect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { Option } from '@nxsys/types/models/Common';
import { validatePhone } from '@nxsys/utils/validator';
import { Form } from 'antd';
import { filter, includes, isEmpty } from 'lodash';
import moment from 'moment';
import React, { Ref, useImperativeHandle, useMemo } from 'react';
import { useSelector } from 'react-redux';

export type CandidateFormValues = {
  title?: Option<string> | null;
  dob?: string;
  forename?: string;
  surname?: string;
  agency?: Option<string> | null;
  paymentCompany?: Option<string> | null;
  email?: string;
  phoneNumber?: string;
  payFrequency?: string | null;
  paymentModel?: Option<string> | null;
  businessType?: Option<string> | null;
};

export type CandidateFormRef = {
  submit: () => void;
  reset: () => void;
};

type CandidateFormProps = {
  formRef?: Ref<CandidateFormRef>;
  onSubmit?: (values: CandidateFormValues) => void;
  defaultValues?: CandidateFormValues;
};

const CandidateForm: React.FC<CandidateFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  const isIreland = payrollCountry === IRELAND;

  const [form] = Form.useForm<CandidateFormValues>();

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
    reset() {
      form.resetFields();
    },
  }));

  const watchPaymentCompany = Form.useWatch('paymentCompany', form);
  const companyPayment = watchPaymentCompany?.fullData;
  const watchPaymentModel = Form.useWatch('paymentModel', form);
  const paymentModelValue = watchPaymentModel?.value ?? '';

  const appStore = useSelector((state: AppState) => state.appStore);
  const defaultPaymentModel = isIreland
    ? paymentModelOptionsIreland
    : (appStore?.appConfigs?.paymentModels ?? []);
  const paymentModelOptions = useMemo(() => {
    // A company running CIS implicitly runs NON_CIS too — but Ireland has no
    // NON_CIS at all, its CIS value is RCT
    const paymentModel =
      !isIreland && includes(companyPayment?.paymentModel, 'CIS')
        ? [...(companyPayment?.paymentModel ?? []), 'NON_CIS']
        : (companyPayment?.paymentModel ?? []);
    return paymentModel && !isEmpty(paymentModel)
      ? filter(defaultPaymentModel, (pm) => includes(paymentModel, pm.value))
      : [];
  }, [companyPayment, defaultPaymentModel, isIreland]);

  const formItems: { label: string; name: keyof CandidateFormValues; required?: boolean; type: string; placeholder?: string; rules?: any[]; options?: any[]; hidden?: boolean; apiType?: string; selectAsyncType?: string; paramFilter?: Record<string, any>; disabled?: boolean }[] = [
    {
      label: messages['common.title'],
      name: 'title',
      required: true,
      type: 'select',
      options: titleOptions,
      placeholder: messages['form.selectTitle'],
    },
    {
      label: messages['common.forename'],
      name: 'forename',
      required: true,
      type: 'text',
      placeholder: messages['form.enterForename'],
    },
    {
      label: messages['common.surname'],
      name: 'surname',
      required: true,
      type: 'text',
      placeholder: messages['form.enterSurname'],
    },
    {
      label: messages['common.dob'],
      name: 'dob',
      type: 'date',
      placeholder: messages['form.selectDate'],
      rules: [
        {
          validator: (_: any, val: any) => {
            if (!val) return Promise.resolve();
            const age = moment().year() - moment(val).year();
            if (age >= 14) return Promise.resolve();
            return Promise.reject(
              new Error(messages['validation.dobMinAge']),
            );
          },
        },
      ],
    },
    {
      label: messages['common.email'],
      name: 'email',
      required: true,
      type: 'text',
      placeholder: messages['form.enterEmail'],
      rules: [
        {
          type: 'email' as const,
          message: messages['form.enterValidEmail'],
        },
      ],
    },
    {
      label: messages['common.phoneNumber'],
      name: 'phoneNumber',
      type: 'phone',
      placeholder: messages['form.enterPhoneNumber'],
      required:
        companyPayment?.autoEnrolSettings?.pensionProviderId ===
        'SMART_PENSION',
      rules: [
        {
          validator: (_: any, value: string) => {
            if (!value) return Promise.resolve();
            if (!validatePhone(value)) {
              return Promise.reject(messages['form.enterValidMobile']);
            }
            return Promise.resolve();
          },
        },
      ],
    },
    {
      label: isBureau
        ? messages['common.employer']
        : messages['common.paymentCompany'],
      name: 'paymentCompany',
      type: 'selectAsync',
      apiType: 'searchCompany',
      selectAsyncType: 'company',
      placeholder: messages['form.selectCompany'],
      paramFilter: { subOnboarding: true },
    },
    ...(!isBureau
      ? [
          {
            label: messages['common.agencyName'],
            name: 'agency' as const,
            type: 'selectAsync',
            apiType: 'searchAgency',
            selectAsyncType: 'agency',
            placeholder: messages['form.selectAgency'],
          },
        ]
      : []),
    {
      label: messages['common.payFrequency'],
      name: 'payFrequency',
      required: true,
      type: 'payFrequencySelect',
      placeholder: messages['form.selectPayFrequency'],
    },
    ...(!isUK
      ? [
          {
            label: messages['common.paymentModel'],
            name: 'paymentModel' as const,
            type: 'select',
            options: paymentModelOptions,
            placeholder: messages['form.selectPaymentModel'],
          },
        ]
      : []),
    // Bureau and Ireland have no business type — Ireland's CIS is RCT, not
    // self-employment
    ...(!isBureau &&
    !isIreland &&
    ['CIS', 'NON_CIS'].includes(paymentModelValue)
      ? [
          {
            label: messages['common.businessType'],
            name: 'businessType' as const,
            type: 'select',
            options: defaultsBusinessTypeOptions,
            placeholder: messages['form.selectBusinessType'],
          },
        ]
      : []),
  ];

  const onFinish = (values: CandidateFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  // A business type belongs to one payment model, so switching model drops it.
  // Only user edits land here — setFieldsValue does not trigger this.
  const onValuesChange = (changedValues: Partial<CandidateFormValues>) => {
    if ('paymentModel' in changedValues) {
      form.setFieldValue('businessType', null);
    }
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      onValuesChange={onValuesChange}
      {...(defaultValues ? { initialValues: defaultValues } : {})}
    >
      {formItems.map((item) => (
        <Form.Item
          key={item.name}
          label={item.label}
          name={item.name}
          rules={[
            {
              required: !!item.required,
              message:
                item.type === 'select' ||
                item.type === 'payFrequencySelect' ||
                item.type === 'selectAsync'
                  ? messages['form.pleaseSelect']
                  : messages['form.pleaseEnter'],
            },
            ...(item.rules ?? []),
          ]}
        >
          {item.type === 'select' ? (
            <AppSelect
              placeholder={item.placeholder}
              options={item.options ?? []}
            />
          ) : item.type === 'date' ? (
            <AppDatePicker
              placeholder={item.placeholder}
              format={'DD/MM/YYYY'}
            />
          ) : item.type === 'phone' ? (
            <AppInput
              placeholder={item.placeholder}
              onChange={(e) => {
                form.setFieldValue(
                  item.name,
                  e.target.value.replace(/\D/g, ''),
                );
              }}
            />
          ) : item.type === 'text' ? (
            <AppInput
              placeholder={item.placeholder}
              type={item.type}
              allowCapitalize={item.name !== 'email'}
            />
          ) : item.type === 'payFrequencySelect' ? (
            <AppPayFrequencySelect
              placeholder={item.placeholder}
              onChange={(val: any) =>
                form.setFieldsValue({ payFrequency: val })
              }
            />
          ) : item.type === 'selectAsync' ? (
            <AppSelectSync
              placeholder={item.placeholder}
              apiType={item.apiType as any}
              type={item.selectAsyncType ?? ''}
              onChangeValue={(value: any) => {
                form.setFieldsValue({ [item.name]: value });
                // A different employer allows a different set of payment
                // models, so drop the stale selection and what depends on it
                if (item.name === 'paymentCompany') {
                  form.setFieldsValue({
                    paymentModel: null,
                    businessType: null,
                  });
                }
              }}
              {...(item.paramFilter ? { paramFilter: item.paramFilter } : {})}
            />
          ) : null}
        </Form.Item>
      ))}
    </AppFormContainer>
  );
};

export default CandidateForm;
